import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { CalendarDays, Building2, MapPin, Video, CheckCircle2 } from "lucide-react";
import { matrixStrings as ms } from "../../lib/matrixStrings";
import { CaptchaField } from "../CaptchaField";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TIME_SLOTS = ["09:00", "10:00", "11:00", "12:00", "13:00", "14:00", "15:00", "16:00"];
const MODE_ICONS = { office: Building2, visit: MapPin, online: Video };
const MODES = { office: { cs: "V kanceláři", en: "Office" }, visit: { cs: "U vás", en: "Your place" }, online: { cs: "Online", en: "Online" } };
const ADVISORS = [
  { id: "raffaele", name: "Raffaele Zirone" },
  { id: "ondrej", name: "Ondřej Šenfeld" },
  { id: "any", name: null },
];

const inputCls =
  "w-full rounded-lg border border-teal-400/25 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-slate-500 backdrop-blur transition-colors duration-200 focus:border-teal-300 focus:outline-none focus:ring-2 focus:ring-teal-400/20";

const chipCls = (active) =>
  `rounded-lg border px-3 py-2.5 text-xs font-semibold transition-all duration-200 ${
    active
      ? "border-teal-300 bg-teal-400/15 text-teal-200"
      : "border-teal-400/20 bg-white/5 text-slate-300 hover:border-teal-300/50"
  }`;

export const MatrixBooking = ({ lang, name, email, phone, scores }) => {
  const [advisor, setAdvisor] = useState("any");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [mode, setMode] = useState("office");
  const [taken, setTaken] = useState([]);
  const [form, setForm] = useState({ name: name || "", email: email || "", phone: phone || "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(false);
  const [capError, setCapError] = useState(null);
  const [captcha, setCaptcha] = useState({ id: null, text: "" });
  const [capRefresh, setCapRefresh] = useState(0);

  const today = new Date().toISOString().split("T")[0];

  useEffect(() => {
    if (!date) return;
    setTime("");
    axios
      .get(`${API}/bookings/slots`, { params: { date } })
      .then((r) => setTaken(r.data.booked || []))
      .catch(() => setTaken([]));
  }, [date]);

  const submit = async (e) => {
    e.preventDefault();
    if (!date || !time) {
      setError(true);
      return;
    }
    if (!captcha.id || captcha.text.trim().length < 4) {
      setCapError("missing");
      return;
    }
    setError(false);
    setCapError(null);
    setSending(true);
    try {
      await axios.post(`${API}/bookings`, {
        advisor, date, time, mode,
        name: form.name, email: form.email, phone: form.phone,
        topic: "Zirsen Analytic Tool",
        matrix: scores,
        lang,
        captchaId: captcha.id, captchaText: captcha.text,
      });
      setDone(true);
    } catch (err) {
      if (err?.response?.data?.detail === "invalid_captcha") {
        setCapError("wrong");
      } else {
        setError(true);
      }
      setCapRefresh((k) => k + 1);
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.94 }}
        animate={{ opacity: 1, scale: 1 }}
        className="mg-glass mx-auto max-w-lg rounded-2xl p-10 text-center"
        data-testid="matrix-booking-done"
      >
        <CheckCircle2 size={52} className="mx-auto text-teal-300" />
        <h2 className="font-heading mg-glow-text mt-5 text-3xl font-extrabold text-white">{ms.booking.doneHeading[lang]}</h2>
        <p className="mt-4 text-sm leading-relaxed text-slate-300">{ms.booking.doneText[lang]}</p>
        <p className="font-mono mt-6 text-sm text-teal-300">{date} · {time} · {MODES[mode][lang]}</p>
        <a href="/" data-testid="matrix-back-home" className="mt-8 inline-block rounded-full border border-teal-400/40 px-6 py-3 text-xs font-bold uppercase tracking-widest text-teal-300 transition-colors duration-200 hover:bg-teal-400/10">
          {ms.booking.backHome[lang]}
        </a>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className="mx-auto max-w-2xl"
      data-testid="matrix-booking"
    >
      <p className="text-center font-mono text-[11px] font-semibold tracking-[0.3em] text-teal-400">{ms.booking.kicker[lang]}</p>
      <h2 className="font-heading mg-glow-text mt-4 text-center text-3xl font-extrabold text-white sm:text-4xl">{ms.booking.heading[lang]}</h2>
      <p className="mx-auto mt-4 max-w-lg text-center text-sm leading-relaxed text-slate-400">{ms.booking.sub[lang]}</p>

      <form
        onSubmit={submit}
        onKeyDown={(e) => {
          if (e.key === "Enter" && e.target.tagName !== "TEXTAREA" && e.target.type !== "submit") e.preventDefault();
        }}
        className="mg-glass mt-10 space-y-6 rounded-2xl p-6 sm:p-8"
        data-testid="matrix-booking-form"
      >
        <div className="grid grid-cols-3 gap-2">
          {ADVISORS.map((a) => (
            <button type="button" key={a.id} onClick={() => setAdvisor(a.id)} className={chipCls(advisor === a.id)} data-testid={`matrix-advisor-${a.id}`}>
              {a.name || (lang === "cs" ? "Kdokoliv" : "Either")}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <CalendarDays size={18} className="shrink-0 text-teal-300" />
          <input type="date" min={today} value={date} onChange={(e) => setDate(e.target.value)} className={`${inputCls} max-w-[220px]`} data-testid="matrix-booking-date" />
        </div>
        <div className="grid grid-cols-4 gap-2 sm:grid-cols-8">
          {TIME_SLOTS.map((slot) => (
            <button
              type="button" key={slot}
              disabled={!date || taken.includes(slot)}
              onClick={() => setTime(slot)}
              data-testid={`matrix-slot-${slot.replace(":", "")}`}
              className={`rounded-lg border py-2 font-mono text-xs font-semibold transition-all duration-150 ${
                time === slot
                  ? "border-teal-300 bg-teal-400 text-[#052E2B]"
                  : taken.includes(slot)
                    ? "cursor-not-allowed border-teal-400/10 bg-white/[0.02] text-slate-600 line-through"
                    : "border-teal-400/20 bg-white/5 text-slate-300 hover:border-teal-300/60 disabled:cursor-not-allowed disabled:text-slate-600"
              }`}
            >
              {slot}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-3 gap-2">
          {Object.entries(MODES).map(([key, label]) => {
            const Icon = MODE_ICONS[key];
            return (
              <button type="button" key={key} onClick={() => setMode(key)} className={`${chipCls(mode === key)} flex items-center justify-center gap-1.5`} data-testid={`matrix-mode-${key}`}>
                <Icon size={14} /> {label[lang]}
              </button>
            );
          })}
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={ms.booking.name[lang]} className={inputCls} data-testid="matrix-booking-name" />
          <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={ms.booking.email[lang]} className={inputCls} data-testid="matrix-booking-email" />
          <input required type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={ms.booking.phone[lang]} className={inputCls} data-testid="matrix-booking-phone" />
        </div>

        <CaptchaField
          onChange={(id, text) => { setCaptcha({ id, text }); setCapError(null); }}
          inputCls={inputCls}
          refreshSignal={capRefresh}
          testidPrefix="matrix-captcha"
        />

        {error && <p className="text-center font-mono text-xs text-red-300" data-testid="matrix-booking-error">{ms.booking.missing[lang]}</p>}
        {capError && <p className="text-center font-mono text-xs text-red-300" data-testid="matrix-captcha-error">{capError === "wrong" ? ms.booking.captchaWrong[lang] : ms.booking.captchaMissing[lang]}</p>}

        <button
          type="submit"
          disabled={sending}
          data-testid="matrix-booking-submit"
          className="w-full rounded-full bg-teal-500 py-4 text-sm font-bold uppercase tracking-widest text-[#052E2B] transition-all duration-200 hover:bg-teal-300 hover:shadow-[0_0_30px_rgba(45,212,191,0.4)] disabled:opacity-50"
        >
          {sending ? ms.booking.sending[lang] : ms.booking.submit[lang]}
        </button>
      </form>
    </motion.div>
  );
};
